import React, { useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useQuery, useMutation } from '@apollo/client';
import { GET_WORK_ORDER } from '../graphql/queries';
import { UPDATE_WORK_ORDER, ASSIGN_WORK_ORDER } from '../graphql/mutations';
import { BULK_UPDATE_CUSTOM_WORK_ORDER_FLAGS } from '../graphql/work-order-flags';
import WorkOrderForm from '../components/work-orders/WorkOrderForm';
import { toast } from 'react-hot-toast';
import { buildEditWorkOrderFormData } from '../utils/work-order-edit-form-data';

export default function EditWorkOrder() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [errorMessage, setErrorMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { loading: queryLoading, error: queryError, data, refetch } = useQuery(GET_WORK_ORDER, {
    variables: { id },
    fetchPolicy: 'network-only',
  });

  const [updateWorkOrder, { loading: mutationLoading }] = useMutation(UPDATE_WORK_ORDER, {
    onError: (error) => {
      console.error('Error updating work order:', error);
      console.error('GraphQL errors:', error.graphQLErrors);
      console.error('Network error:', error.networkError);
      setErrorMessage(error.message || 'An error occurred while updating the work order. Please try again.');
      toast.error('Failed to update work order');
      setIsSubmitting(false);
    }
  });
  const [bulkUpdateFlags] = useMutation(BULK_UPDATE_CUSTOM_WORK_ORDER_FLAGS);

  const [assignWorkOrder] = useMutation(ASSIGN_WORK_ORDER, {
    onCompleted: async () => {
      try {
        await refetch();
      } catch (error) {
        // Refetch is best-effort after assignment
      }
    },
    onError: (error) => {
      setErrorMessage(error.message || 'An error occurred while assigning the work order. Please try again.');
      toast.error('Failed to assign work order');
      setIsSubmitting(false);
    }
  });

  const handleSubmit = async (formData) => {
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      const { description, isUrgent, isAfterHours, workOrderType, photos, assignedToId, assignedTeamId, assignedRoles, dueDate } = formData;
      const nextAssignedToId = assignedTeamId ? null : ((assignedToId && assignedToId !== '') ? assignedToId : null);
      const nextAssignedTeamId = assignedTeamId || null;

      const assignmentChanged =
        String(nextAssignedToId || '') !== String(workOrder.assignedToId || '') ||
        String(nextAssignedTeamId || '') !== String(workOrder.assignedTeamId || '') ||
        JSON.stringify(assignedRoles) !== JSON.stringify(workOrder.assignedRoles || []);

      if (assignmentChanged) {
        await assignWorkOrder({
          variables: {
            input: {
              id,
              assignedToId: nextAssignedToId,
              assignedTeamId: nextAssignedTeamId,
              assignedRoles: assignedRoles || []
            }
          }
        });
      }

      let dueDateValue = null;
      if (dueDate) {
        const [year, month, day] = dueDate.split('-').map(Number);
        dueDateValue = new Date(year, month - 1, day, 12, 0, 0);
      }

      await updateWorkOrder({
        variables: {
          id,
          input: {
            id,
            description,
            isUrgent,
            isAfterHours,
            workOrderType,
            photos: photos,
            dueDate: dueDateValue
          }
        }
      });

      const selectedTemplateIds = formData.workOrderFlagTemplateIds || [];
      const selectedTemplateIdSet = new Set(selectedTemplateIds);
      const selectedTemplateFlags = selectedTemplateIds.map((templateId, index) => ({
        workOrderId: id,
        templateId,
        flagType: 'custom',
        flagValue: 'template',
        sortOrder: index
      }));

      // Keep one-off flags that are not tied to a selected template value
      const selectedTemplateValues = new Set(
        (workOrder.customFlags || [])
          .filter(flag => selectedTemplateIdSet.has(flag.templateId || flag.template?.id))
          .map(flag => (flag.flagValue || '').toLowerCase())
      );

      const manualFlags = (workOrder.customFlags || [])
        .filter(flag => !flag.templateId && !flag.template?.id)
        .filter(flag => !selectedTemplateValues.has((flag.flagValue || '').toLowerCase()))
        .map((flag, index) => ({
          workOrderId: id,
          flagType: flag.flagType || 'custom',
          flagValue: flag.flagValue,
          flagColor: flag.flagColor,
          sortOrder: selectedTemplateFlags.length + index
        }));

      try {
        await bulkUpdateFlags({
          variables: {
            input: {
              workOrderId: id,
              flags: [...selectedTemplateFlags, ...manualFlags]
            }
          }
        });
      } catch (flagError) {
        console.error('Error updating work order flags:', flagError);
        setErrorMessage(flagError.message || 'Work order saved, but flags could not be updated.');
        toast.error(flagError.message || 'Failed to update work order flags');
        setIsSubmitting(false);
        return;
      }

      toast.success('Work order updated successfully');
      await refetch();
      navigate(`/dashboard/work-orders/${id}`);
    } catch (error) {
      setErrorMessage(error.message || 'Failed to process your request. Please try again.');
      setIsSubmitting(false);
      toast.error('Failed to update work order');
    }
  };

  if (queryLoading) return (
    <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
    </div>
  );

  if (queryError) return (
    <div className="bg-red-50 p-4 rounded-md">
      <div className="flex">
        <div className="flex-shrink-0">
          <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
        <div className="ml-3">
          <h3 className="text-sm font-medium text-red-800">Error loading work order</h3>
          <div className="mt-2 text-sm text-red-700">
            <p>{queryError.message}</p>
          </div>
        </div>
      </div>
    </div>
  );

  const workOrder = data?.workOrder;
  if (!workOrder) return null;

  const initialData = buildEditWorkOrderFormData(workOrder);

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-4">
        <Link to={`/dashboard/work-orders/${id}`} className="inline-flex items-center text-gray-600 hover:text-gray-900">
          <svg className="h-5 w-5 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          Back to Work Order
        </Link>
        <h1 className="text-2xl font-semibold text-gray-900">Edit Work Order</h1>
      </div>

      {errorMessage && (
        <div className="bg-red-50 p-4 rounded-md">
          <div className="flex">
            <div className="flex-shrink-0">
              <svg className="h-5 w-5 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-red-800">Error</h3>
              <div className="mt-2 text-sm text-red-700">
                <p>{errorMessage}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white shadow overflow-hidden rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <WorkOrderForm
            initialData={initialData}
            onSubmit={handleSubmit}
            isLoading={mutationLoading || isSubmitting}
            showAssignment={true}
          />
        </div>
      </div>
    </div>
  );
}
